import { getSession } from './supabase';
import { errorMessage, isRecord } from './errors';

/** An edge function answered with a non-success status. The message is the server's own error text. */
export class EdgeFunctionError extends Error {
  constructor(
    readonly functionName: string,
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'EdgeFunctionError';
  }
}

const MAX_RAW_ERROR_CHARS = 200;

type ParsedBody = { ok: true; value: unknown } | { ok: false };

function parseJson(raw: string): ParsedBody {
  try {
    return { ok: true, value: JSON.parse(raw) };
  } catch {
    return { ok: false };
  }
}

/** The server's error text: `{ error }` from our functions, `{ message }` / `{ msg }` from the Supabase gateway, else the raw body. */
function serverErrorText(body: ParsedBody, raw: string, status: number): string {
  if (body.ok && isRecord(body.value)) {
    for (const key of ['error', 'message', 'msg']) {
      const value = body.value[key];
      if (typeof value === 'string' && value) return `${value} (${status})`;
    }
  }
  const text = raw.trim();
  if (text) return `${text.slice(0, MAX_RAW_ERROR_CHARS)} (${status})`;
  return `HTTP ${status}`;
}

/**
 * POST `body` to a Supabase edge function as the signed-in user (their access
 * token as the bearer) and return the parsed JSON response. Throws with the
 * server's error text when the call does not succeed.
 */
export async function invokeEdgeFunction(name: string, body: Record<string, unknown>, expectedOwnerId?: string): Promise<unknown> {
  const {
    data: { session },
  } = await getSession();
  if (!session) throw new Error('you are signed out; sign in and try again');
  if (expectedOwnerId !== undefined && session.user.id !== expectedOwnerId) throw new Error('the signed-in account changed; sign in as the input owner');

  let response: Response;
  try {
    response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${name}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
  } catch (err) {
    throw new Error(`could not reach ${name}: ${errorMessage(err)}`);
  }

  const raw = await response.text();
  const parsed = parseJson(raw);
  if (!response.ok) {
    throw new EdgeFunctionError(name, response.status, serverErrorText(parsed, raw, response.status));
  }
  if (!parsed.ok) {
    throw new EdgeFunctionError(name, response.status, `${name} returned a response that is not JSON`);
  }
  return parsed.value;
}
